import { productApi } from "#/api/productApi";
import ActionButton from "#/components/buttons/ActionButton";
import PermissionTooltip from "#/components/ui/PermissionTooltip";
import { usePermission } from "#/hooks/usePermission";
import {
  AlertCondition,
  AlertNotificationMode,
  type SubClientCategoryAlert,
} from "#/types/subClient";
import {
  Accordion,
  NumberInput,
  Select,
} from "@mantine/core";
import { IconPlus, IconTrash } from "@tabler/icons-react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

interface Props {
  alerts: SubClientCategoryAlert[];
  onChange: (alerts: SubClientCategoryAlert[]) => void;
}

const AlertsAccordion = ({ alerts, onChange }: Props) => {
  const { t } = useTranslation();
  const { canWrite, canDelete } = usePermission();

  const { data: products = [], isPending: productsPending } = useQuery({
    queryKey: ["products-for-alerts"],
    queryFn: async () => {
      const res = await productApi.getAll({ limit: 1000, page: 1 });
      return res.data.data;
    },
    staleTime: 1000 * 60 * 5,
  });

  const conditionOptions = [
    { value: AlertCondition.MORE_THAN, label: t("subClients.alerts.moreThan") },
    { value: AlertCondition.LESS_THAN, label: t("subClients.alerts.lessThan") },
  ];

  const notificationModeOptions = [
    {
      value: AlertNotificationMode.ON_STATE_CHANGE,
      label: t("subClients.alerts.onStateChange"),
    },
    {
      value: AlertNotificationMode.EVERY_SCAN,
      label: t("subClients.alerts.everyScan"),
    },
  ];

  const productOptions = products.map((p) => ({
    value: String(p.id),
    label: p.name ?? `#${p.id}`,
  }));

  const handleAdd = (e: React.MouseEvent) => {
    e.stopPropagation();
    onChange([
      ...alerts,
      {
        productId: 0,
        condition: AlertCondition.LESS_THAN,
        threshold: 0,
        notificationMode: AlertNotificationMode.ON_STATE_CHANGE,
      },
    ]);
  };

  const updateRow = (index: number, patch: Partial<SubClientCategoryAlert>) => {
    const next = alerts.map((row, i) => (i === index ? { ...row, ...patch } : row));
    onChange(next);
  };

  const removeRow = (index: number) => {
    onChange(alerts.filter((_, i) => i !== index));
  };

  return (
    <Accordion.Item value="alerts">
      <Accordion.Control>
        <div className="flex items-center gap-2">
          <span>{t("subClients.sections.alerts")}</span>
          <PermissionTooltip disabled={!canWrite()}>
            <ActionButton
              color="black"
              size="xs"
              icon={IconPlus}
              disabled={!canWrite()}
              onClick={handleAdd}
            />
          </PermissionTooltip>
        </div>
      </Accordion.Control>
      <Accordion.Panel>
        <div className="text-sm text-gray-600 mb-3">
          {t("subClients.sections.alertsHint")}
        </div>
        {alerts.length === 0 ? (
          <div className="text-sm text-gray-500">
            {t("subClients.alerts.empty")}
          </div>
        ) : (
          <div className="w-full flex flex-col gap-2">
            {alerts.map((alert, index) => (
              <div
                key={index}
                className="w-full grid grid-cols-[auto_1fr_200px_140px_240px_auto] gap-2 items-end"
              >
                <span className="bg-black/5 font-bold text-black/70 rounded-full w-8 h-8 flex items-center justify-center">
                  {index + 1}
                </span>

                <Select
                  label={t("subClients.alerts.category")}
                  placeholder={t("subClients.alerts.categoryPlaceholder")}
                  data={productOptions}
                  value={alert.productId ? String(alert.productId) : null}
                  searchable
                  disabled={!canWrite() || productsPending}
                  onChange={(val) => {
                    const id = val ? Number(val) : 0;
                    const product = products.find((p) => p.id === id);
                    updateRow(index, {
                      productId: id,
                      productName: product?.name,
                    });
                  }}
                />

                <Select
                  label={t("subClients.alerts.condition")}
                  data={conditionOptions}
                  value={alert.condition}
                  disabled={!canWrite()}
                  onChange={(val) => {
                    if (val) {
                      updateRow(index, { condition: val as AlertCondition });
                    }
                  }}
                />

                <NumberInput
                  label={t("subClients.alerts.threshold")}
                  min={0}
                  allowDecimal={false}
                  allowNegative={false}
                  disabled={!canWrite()}
                  value={alert.threshold}
                  onChange={(val) => {
                    updateRow(index, {
                      threshold: typeof val === "number" ? val : 0,
                    });
                  }}
                />

                <Select
                  label={t("subClients.alerts.notificationMode")}
                  data={notificationModeOptions}
                  value={alert.notificationMode}
                  disabled={!canWrite()}
                  onChange={(val) => {
                    if (val) {
                      updateRow(index, {
                        notificationMode: val as AlertNotificationMode,
                      });
                    }
                  }}
                />

                <PermissionTooltip disabled={!canDelete()}>
                  <ActionButton
                    icon={IconTrash}
                    variant="white"
                    color="red.5"
                    disabled={!canDelete()}
                    onClick={() => removeRow(index)}
                  />
                </PermissionTooltip>
              </div>
            ))}
          </div>
        )}
      </Accordion.Panel>
    </Accordion.Item>
  );
};

export default AlertsAccordion;
